import { DeleteOutlined, EditOutlined, PlusOutlined, TagOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import {
  FALLBACK_CURRENCY,
  fetchEventCurrency as fetchEventCurrencyFromApi,
} from '../config/eventCurrency';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface TicketType {
  id: string;
  name: string;
  description?: string;
  price: number;
  quantity: number;
  available: number;
  startSale?: string;
  endSale?: string;
  templateId?: string;
  isPublic: boolean;
}

interface TicketTemplate {
  id: string;
  name: string;
  description?: string;
  defaultPrice: number;
  color?: string;
}

interface Event {
  id: string;
  title: string;
}

interface Props {
  event: Event | null;
  visible: boolean;
  onClose: () => void;
}

const EventTicketTypes: React.FC<Props> = ({ event, visible, onClose }) => {
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [templates, setTemplates] = useState<TicketTemplate[]>([]);
  const [currency, setCurrency] = useState<string>('EUR');
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingType, setEditingType] = useState<TicketType | null>(null);
  const [visibilitySaving, setVisibilitySaving] = useState<ReadonlySet<string>>(new Set());
  const [form] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    if (event && visible) {
      fetchTicketTypes();
      fetchTemplates();
      fetchEventCurrency();
    }
  }, [event, visible]);

  /**
   * A pénznem a közös segédből (#111 utómunka) — korábban ez a másolat egy volt
   * a négyből. A megjelenítési tartalék itt marad `EUR`: egy szerkesztő űrlap
   * mezőjének mindenképp kell egy felirat.
   */
  const fetchEventCurrency = async () => {
    if (!event) return;
    setCurrency((await fetchEventCurrencyFromApi(event.id)) ?? FALLBACK_CURRENCY);
  };

  const fetchTicketTypes = async () => {
    if (!event) return;

    setLoading(true);
    try {
      const response = await api.get(`/tickets/types/event/${event.id}`);
      setTicketTypes(response.data || []);
    } catch (error) {
      message.error(t('eventTicketTypes.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/ticket-templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Templates betöltése sikertelen');
    }
  };

  const handleCreate = () => {
    setEditingType(null);
    form.resetFields();
    form.setFieldsValue({
      quantity: 100,
      price: 50,
      isPublic: true,
    });
    setModalVisible(true);
  };

  const handleCreateFromTemplate = (template: TicketTemplate) => {
    setEditingType(null);
    form.resetFields();
    form.setFieldsValue({
      templateId: template.id,
      name: template.name,
      description: template.description,
      price: template.defaultPrice,
      quantity: 100,
      isPublic: true,
    });
    setModalVisible(true);
  };

  const handleEdit = (record: TicketType) => {
    setEditingType(record);
    form.setFieldsValue({
      ...record,
      salesPeriod: record.startSale ? [dayjs(record.startSale), dayjs(record.endSale)] : undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/tickets/types/${id}`);
      message.success(t('eventTicketTypes.deleted'));
      fetchTicketTypes();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('eventTicketTypes.deleteFailed'));
    }
  };

  const setRowVisibility = (id: string, isPublic: boolean) =>
    setTicketTypes((rows) => rows.map((row) => (row.id === id ? { ...row, isPublic } : row)));

  const markSaving = (id: string, saving: boolean) =>
    setVisibilitySaving((ids) => {
      const next = new Set(ids);
      if (saving) next.add(id);
      else next.delete(id);
      return next;
    });

  // The row flips at once and flips back if the save fails.
  const handleVisibilityChange = async (record: TicketType, isPublic: boolean) => {
    markSaving(record.id, true);
    setRowVisibility(record.id, isPublic);
    try {
      await api.patch(`/tickets/types/${record.id}`, { isPublic });
      message.success(t('eventTicketTypes.updated'));
    } catch (error: any) {
      setRowVisibility(record.id, record.isPublic);
      message.error(error.response?.data?.message ?? t('eventTicketTypes.operationFailed'));
    } finally {
      markSaving(record.id, false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!event) return;

    try {
      const data: any = {
        ...values,
      };

      if (values.salesPeriod) {
        data.startSale = values.salesPeriod[0].toISOString();
        data.endSale = values.salesPeriod[1].toISOString();
        delete data.salesPeriod;
      }

      if (editingType) {
        // For update, only send allowed fields
        const updateData: any = {
          name: data.name,
          description: data.description,
          price: data.price,
          isPublic: data.isPublic,
        };

        // Don't send quantity on update, the backend doesn't accept it
        // If user changes quantity in the form, we should update 'available' instead
        // but only if they're increasing it (we can't decrease total quantity)
        if (data.quantity && data.quantity !== editingType.quantity) {
          // Calculate the difference and adjust available
          const diff = data.quantity - editingType.quantity;
          if (diff > 0) {
            // If increasing total quantity, increase available by the same amount
            updateData.available = editingType.available + diff;
          }
        }

        if (data.startSale) updateData.startSale = data.startSale;
        if (data.endSale) updateData.endSale = data.endSale;

        await api.patch(`/tickets/types/${editingType.id}`, updateData);
        message.success(t('eventTicketTypes.updated'));
      } else {
        // For create, include eventId and templateId
        data.eventId = event.id;
        await api.post('/tickets/types', data);
        message.success(t('eventTicketTypes.created'));
      }

      setModalVisible(false);
      fetchTicketTypes();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('eventTicketTypes.operationFailed'));
    }
  };

  const columns: ColumnsType<TicketType> = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <TagOutlined />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: t('eventTicketTypes.columnPrice'),
      dataIndex: 'price',
      key: 'price',
      render: (price) => `${price.toFixed(2)} ${currency}`,
    },
    {
      title: t('eventTicketTypes.columnQuantity'),
      key: 'quantity',
      render: (_, record) => (
        <Space>
          <span>
            {record.available} / {record.quantity}
          </span>
          {record.available === 0 && <Tag color="red">{t('eventTicketTypes.soldOut')}</Tag>}
        </Space>
      ),
    },
    {
      title: t('eventTicketTypes.columnSalesPeriod'),
      key: 'salesPeriod',
      render: (_, record) => {
        if (!record.startSale) return t('eventTicketTypes.alwaysAvailable');
        return (
          <Space direction="vertical" size={0}>
            <small>{dayjs(record.startSale).format('YYYY.MM.DD HH:mm')}</small>
            <small>{dayjs(record.endSale).format('YYYY.MM.DD HH:mm')}</small>
          </Space>
        );
      },
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('eventTicketTypes.visibleOnEventPage')}>
            <Switch
              size="small"
              aria-label={t('eventTicketTypes.visibleOnEventPage')}
              checked={record.isPublic}
              loading={visibilitySaving.has(record.id)}
              disabled={visibilitySaving.has(record.id)}
              onChange={(checked) => handleVisibilityChange(record, checked)}
            />
          </Tooltip>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={t('eventTicketTypes.manageTitle', { event: event?.title || '' })}
        open={visible}
        onCancel={onClose}
        width={900}
        footer={[
          <Button key="close" onClick={onClose}>
            {t('common.close')}
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('eventTicketTypes.newType')}
            </Button>

            {templates.length > 0 && (
              <Select
                placeholder={t('eventTicketTypes.fromTemplate')}
                style={{ width: 200 }}
                onChange={(templateId) => {
                  const template = templates.find((t) => t.id === templateId);
                  if (template) handleCreateFromTemplate(template);
                }}
                value={undefined}
              >
                {templates.map((template) => (
                  <Option key={template.id} value={template.id}>
                    <Space>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: template.color || '#ccc',
                          borderRadius: 2,
                          display: 'inline-block',
                        }}
                      />
                      {template.name}
                    </Space>
                  </Option>
                ))}
              </Select>
            )}
          </Space>

          <Table
            columns={columns}
            dataSource={ticketTypes}
            rowKey="id"
            loading={loading}
            pagination={false}
          />
        </Space>
      </Modal>

      <Modal
        title={editingType ? t('eventTicketTypes.editTitle') : t('eventTicketTypes.newType')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="templateId" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="name"
            label={t('common.name')}
            rules={[{ required: true, message: t('common.requiredField') }]}
          >
            <Input placeholder={t('eventTicketTypes.namePlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={t('common.description')}>
            <Input.TextArea rows={3} placeholder={t('eventTicketTypes.descriptionPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="price"
            label={t('eventTicketTypes.priceLabel', { currency })}
            rules={[{ required: true, message: t('common.requiredField') }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="50" addonAfter={currency} />
          </Form.Item>

          <Form.Item
            name="quantity"
            label={t('eventTicketTypes.columnQuantity')}
            rules={[{ required: true, message: t('common.requiredField') }]}
            extra={editingType ? t('eventTicketTypes.quantityHint') : undefined}
          >
            <InputNumber
              min={editingType ? editingType.quantity : 1}
              style={{ width: '100%' }}
              placeholder="100"
            />
          </Form.Item>

          <Form.Item name="salesPeriod" label={t('eventTicketTypes.salesPeriodLabel')}>
            <RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="isPublic"
            label={t('eventTicketTypes.visibleOnEventPage')}
            valuePropName="checked"
            extra={t('eventTicketTypes.visibleOnEventPageHint')}
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingType ? t('eventTicketTypes.save') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('eventTicketTypes.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EventTicketTypes;
